import { useEffect, useRef, useState } from "react";
import { Calendar, DateField, DatePicker as HeroUIDatePicker, TimeField } from "@heroui/react";
import {
  CalendarDateTime,
  type Time,
} from "@internationalized/date";
import { I18nProvider } from "react-aria-components";
import {
  displayCalendarToWire,
  displayTimeValue,
  isCompleteDateTime,
  normalizeClock,
  nowTimeOnDisplayDay,
  nowWire,
  todayWireKeepingTime,
  wireBoundToDisplay,
  wireToDisplayCalendar,
} from "@/lib/datetime-input";
import { useTimezoneStore } from "@/stores/timezone";

type DateTimePickerProps = {
  /** UTC wire value: `YYYY-MM-DD HH:MM:00`. */
  value: string;
  /** Called with UTC wire. */
  onChange: (value: string) => void;
  disabled?: boolean;
  min?: string;
  max?: string;
  clearable?: boolean;
};

function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

/**
 * Date+time control. Displays in the app timezone preference (UTC or Local),
 * always commits UTC wire `YYYY-MM-DD HH:MM:00` to the parent.
 */
export function DateTimePicker({
  value,
  onChange,
  disabled = false,
  min,
  max,
  clearable = false,
}: DateTimePickerProps) {
  const mode = useTimezoneStore((s) => s.mode);
  const zoneLabel = mode === "utc" ? "UTC" : "Local";

  const fromProps = wireToDisplayCalendar(value, mode);
  const [draft, setDraft] = useState<CalendarDateTime | null>(fromProps);
  const focusedRef = useRef(false);
  const lastValidWire = useRef(value);

  // Sync from parent when not mid-edit (or when timezone preference changes).
  useEffect(() => {
    if (focusedRef.current) return;
    setDraft(wireToDisplayCalendar(value, mode));
    if (value) lastValidWire.current = value;
  }, [value, mode]);

  function commitWire(wire: string) {
    lastValidWire.current = wire;
    onChange(wire);
  }

  function commitDisplay(next: CalendarDateTime) {
    const normalized = normalizeClock(next);
    setDraft(normalized);
    if (!isCompleteDateTime(normalized)) return;
    commitWire(displayCalendarToWire(normalized, mode));
  }

  const timeValue: Time | null = displayTimeValue(draft);
  const hasDate = draft != null && isCompleteDateTime(draft);

  const todayOutOfRange = (() => {
    const today = wireToDisplayCalendar(todayWireKeepingTime("", mode), mode);
    if (!today) return false;
    const minV = wireBoundToDisplay(min, mode, false);
    const maxV = wireBoundToDisplay(max, mode, true);
    if (minV && today.compare(minV) < 0) return true;
    if (maxV && today.compare(maxV) > 0) return true;
    return false;
  })();

  return (
    <I18nProvider locale="en-GB">
      <HeroUIDatePicker
        className="app-date-picker app-date-time-picker w-full"
        granularity="minute"
        hourCycle={24}
        hideTimeZone
        value={draft}
        placeholderValue={
          fromProps ??
          wireToDisplayCalendar(lastValidWire.current, mode) ??
          wireToDisplayCalendar(nowWire(), mode) ??
          undefined
        }
        onFocusChange={(isFocused) => {
          focusedRef.current = isFocused;
          if (!isFocused) {
            // Blur: restore last valid if the field was left incomplete (null / partial year).
            const restored = wireToDisplayCalendar(lastValidWire.current || value, mode);
            if (!draft || !isCompleteDateTime(draft)) {
              setDraft(restored);
            } else {
              commitDisplay(draft);
            }
          }
        }}
        onChange={(next) => {
          // Incomplete segment edits (e.g. clearing year to type 2024) emit null.
          // Keep the previous draft so day/month/time don't collapse to placeholders.
          if (!next) return;
          commitDisplay(next);
        }}
        isDisabled={disabled}
        minValue={wireBoundToDisplay(min, mode, false)}
        maxValue={wireBoundToDisplay(max, mode, true)}
        aria-label={`Date and time ${zoneLabel}`}
      >
        <DateField.Group fullWidth>
          <DateField.Input>
            {(segment) => <DateField.Segment segment={segment} />}
          </DateField.Input>
          <DateField.Suffix>
            <HeroUIDatePicker.Trigger aria-label="Open calendar">
              <HeroUIDatePicker.TriggerIndicator />
            </HeroUIDatePicker.Trigger>
          </DateField.Suffix>
        </DateField.Group>

        <HeroUIDatePicker.Popover className="app-date-picker__popover">
          <Calendar aria-label="Choose date">
            <Calendar.Header>
              <Calendar.YearPickerTrigger>
                <Calendar.YearPickerTriggerHeading />
                <Calendar.YearPickerTriggerIndicator />
              </Calendar.YearPickerTrigger>
              <Calendar.NavButton slot="previous" />
              <Calendar.NavButton slot="next" />
            </Calendar.Header>
            <Calendar.Grid>
              <Calendar.GridHeader>
                {(day) => <Calendar.HeaderCell>{day}</Calendar.HeaderCell>}
              </Calendar.GridHeader>
              <Calendar.GridBody>{(date) => <Calendar.Cell date={date} />}</Calendar.GridBody>
            </Calendar.Grid>
            <Calendar.YearPickerGrid visibleYears={30}>
              <Calendar.YearPickerGridBody>
                {(year) => <Calendar.YearPickerCell year={year.year} />}
              </Calendar.YearPickerGridBody>
            </Calendar.YearPickerGrid>
          </Calendar>

          <div className="flex flex-col gap-1.5 border-t border-[var(--divider)] px-3 py-2.5">
            <div className="flex items-center justify-between gap-2">
              <span className="text-[10px] font-medium uppercase tracking-wide text-[var(--text-muted)]">
                Time ({zoneLabel})
              </span>
              <button
                type="button"
                className="text-xs font-medium text-[var(--primary)] hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-40"
                disabled={disabled}
                aria-label={`Set time to now ${zoneLabel}`}
                onClick={() => {
                  const wire = value
                    ? nowTimeOnDisplayDay(value, mode)
                    : nowWire();
                  setDraft(wireToDisplayCalendar(wire, mode));
                  commitWire(wire);
                }}
              >
                Now
              </button>
            </div>
            <TimeField
              className="app-date-time-picker__time w-full"
              granularity="minute"
              hourCycle={24}
              value={timeValue}
              isDisabled={disabled || !hasDate}
              aria-label={`Time ${zoneLabel}`}
              onChange={(next) => {
                if (!draft || !isCompleteDateTime(draft)) return;
                // Empty time → 00:00
                if (!next) {
                  commitDisplay(new CalendarDateTime(draft.year, draft.month, draft.day, 0, 0));
                  return;
                }
                const hour = Number.isFinite(next.hour) ? next.hour : 0;
                const minute = Number.isFinite(next.minute) ? next.minute : 0;
                commitDisplay(
                  new CalendarDateTime(draft.year, draft.month, draft.day, hour, minute)
                );
              }}
            >
              <TimeField.Group fullWidth>
                <TimeField.Input>
                  {(segment) => <TimeField.Segment segment={segment} />}
                </TimeField.Input>
              </TimeField.Group>
            </TimeField>
          </div>

          <div className="flex items-center justify-between gap-2 border-t border-[var(--divider)] px-3 py-2">
            <button
              type="button"
              className="text-xs font-medium text-[var(--primary)] hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-40"
              disabled={disabled || todayOutOfRange}
              aria-label={`Select today ${zoneLabel}`}
              onClick={() => {
                const wire = todayWireKeepingTime(value || lastValidWire.current, mode);
                setDraft(wireToDisplayCalendar(wire, mode));
                commitWire(wire);
              }}
            >
              Today
            </button>
            {clearable && value ? (
              <button
                type="button"
                className="text-xs font-medium text-[var(--text-muted)] hover:text-[var(--text-secondary)]"
                disabled={disabled}
                onClick={() => {
                  setDraft(null);
                  lastValidWire.current = "";
                  onChange("");
                }}
              >
                Clear
              </button>
            ) : (
              <span />
            )}
          </div>
        </HeroUIDatePicker.Popover>
      </HeroUIDatePicker>
    </I18nProvider>
  );
}

// Silence unused if tree-shaken oddly — pad2 kept for potential segment helpers.
void pad2;
